import React, { useState, useEffect, useRef, useCallback } from "react"
import "./chatNudge.scss"

// Small speech-bubble chat glyph.
const ChatGlyph = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.9-.9L3 21l1.4-4.6a8.5 8.5 0 0 1-1-4A8.38 8.38 0 0 1 12 4a8.38 8.38 0 0 1 9 7.5Z" />
    <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" />
  </svg>
)

/**
 * Chat launcher that gates the GTM-injected Zoho SalesIQ widget.
 *
 * SalesIQ's "Tripzy" bot auto-opens a full-screen chat iframe on load and
 * buries the page (worst on phones), and none of the SalesIQ JS methods close
 * it reliably. So chatNudge.scss hides Zoho's own window + float button until
 * <html> gets the `.siq-live` class. This component shows a lightweight,
 * blinking "Can I help with your booking?" nudge instead; tapping it adds the
 * class (un-gating the widget) and opens the real chat. From that point on the
 * SalesIQ widget behaves natively for the rest of the session.
 */
const ChatNudge = () => {
  const [launched, setLaunched] = useState(false)
  const [showGreeting, setShowGreeting] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const pollRef = useRef(null)

  // Reveal the greeting a beat after load so it reads as an invitation, not a
  // jump-scare, then auto-tuck it back to just the button so it never sits on
  // top of the page content.
  useEffect(() => {
    const showT = setTimeout(() => setShowGreeting(true), 1600)
    const hideT = setTimeout(() => setShowGreeting(false), 9000)
    return () => { clearTimeout(showT); clearTimeout(hideT) }
  }, [])

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current) }, [])

  const openChat = useCallback(() => {
    if (connecting) return
    // Un-gate Zoho's window and open it. SalesIQ's open() is flaky (a single
    // call often leaves the window visibility:hidden), so pump it until the
    // window is actually on screen. Only hand off (unmount the nudge) once the
    // chat is really visible — if SalesIQ never loads (adblock / GTM blocked),
    // re-gate and keep the nudge so the visitor can retry instead of being
    // stranded with no chat and no button.
    setConnecting(true)
    setShowGreeting(false)
    document.documentElement.classList.add("siq-live")

    const isVisible = () => {
      const w = document.getElementById("siq_chatwindow")
      if (!w) return false
      const cs = window.getComputedStyle(w)
      return cs.display !== "none" && cs.visibility !== "hidden" && w.offsetHeight > 0
    }
    const open = () => {
      const siq = window.$zoho && window.$zoho.salesiq
      try { siq && siq.floatwindow && siq.floatwindow.open && siq.floatwindow.open() } catch (e) { /* ignore */ }
    }

    let tries = 0
    open()
    pollRef.current = setInterval(() => {
      tries += 1
      if (isVisible()) {
        clearInterval(pollRef.current)
        setLaunched(true) // hand off to the native widget only once it's shown
        return
      }
      open()
      if (tries === 8) {
        const fb = document.getElementById("zsiq_float")
        if (fb) { try { fb.click() } catch (e) { /* ignore */ } }
      }
      if (tries > 40) {
        clearInterval(pollRef.current)
        document.documentElement.classList.remove("siq-live")
        setConnecting(false)
      }
    }, 300)
  }, [connecting])

  if (launched) return null

  return (
    <div className="chat-nudge">
      <button
        type="button"
        className="chat-nudge__btn"
        onClick={openChat}
        aria-label="Chat with us about your booking"
        aria-busy={connecting}
      >
        {!connecting && <span className="chat-nudge__ping" aria-hidden="true" />}
        <ChatGlyph />
      </button>

      {showGreeting && (
        <div
          className="chat-nudge__bubble"
          role="button"
          tabIndex={0}
          onClick={openChat}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openChat() }}
        >
          <span className="chat-nudge__bubble-title">Can I help with your booking?</span>
          <span className="chat-nudge__bubble-sub">Chat with us — we reply fast</span>
          <button
            type="button"
            className="chat-nudge__bubble-x"
            aria-label="Dismiss"
            onClick={(e) => { e.stopPropagation(); setShowGreeting(false) }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  )
}

export default ChatNudge
